import React, { FC, useEffect, useState } from "react";
import {
  Stack,
  Button,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useAppDispatch } from "../../../store";
import { fetchPlayersWithRegistrations } from "../../../features/player/playerSlice";
import {
  listenToMatchs,
  createMatch,
} from "../../../features/matchs/matchsSlice";
import BoardSelector from "./BoardSelector";
import RoundSelector from "./RoundSelector";
import MatchPlayerSelector from "./PlayerSelector";
import MatchsList from "./MatchsList";
import { Matchs } from "../../../types";

const AdminMatchs: FC = () => {
  const dispatch = useAppDispatch();

  const [currentBoardId, setCurrentBoardId] = useState<string>("");
  const [currentRoundId, setCurrentRoundId] = useState<string>("");
  const [playersIds, setPlayersIds] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [error, setError] = useState<string | null>(null);
  const [showAllPlayers, setShowAllPlayers] = useState(false);

  useEffect(() => {
    dispatch(fetchPlayersWithRegistrations());
    const unsubscribe = dispatch(listenToMatchs());
    return unsubscribe;
  }, [dispatch]);

  const handleCreateMatch = async () => {
    setError(null);

    // Validation rapide : board, round et au moins un joueur sélectionné
    if (!currentBoardId || !currentRoundId) {
      setError("Veuillez sélectionner un tableau et un tour");
      return;
    }

    if (playersIds.every((id) => id === null)) {
      setError("Veuillez sélectionner au moins un joueur");
      return;
    }

    const newMatch: Matchs = {
      dateCreation: new Date(),
      status: "CREE",
      tableauId: currentBoardId,
      tourId: currentRoundId,
      matchsPlayerParticipations: playersIds
        .filter((id) => id !== null)
        .map((id, idx) => ({
          ordre: idx + 1,
          playersId: id!,
        })),
    };

    try {
      console.log("newMatch", newMatch);
      await dispatch(createMatch({ match: newMatch }));
      // Réinitialiser la sélection des joueurs après création
      setPlayersIds([null, null, null]);
    } catch (err) {
      setError("Erreur lors de la création du match");
    }
  };

  return (
    <Stack spacing={2} direction="column">
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={2}>
        <BoardSelector
          value={currentBoardId}
          handleBoardChange={setCurrentBoardId}
        />
        <RoundSelector
          value={currentRoundId}
          handleRoundChange={setCurrentRoundId}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showAllPlayers}
              onChange={(_, checked) => setShowAllPlayers(checked)}
              color="primary"
            />
          }
          label="Afficher tous les joueurs inscrits à au moins un tableau"
          sx={{ ml: 2, whiteSpace: "nowrap" }}
        />
      </Stack>

      <Stack direction="row" alignItems="stretch" spacing={2} width="100%">
        {playersIds.map((playerId, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              minWidth: 180,
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <MatchPlayerSelector
              label={`Joueur ${index + 1}`}
              currentPlayerId={playerId}
              currentBoardId={currentBoardId}
              showAllPlayers={showAllPlayers}
              onPlayerSelect={(player) =>
                setPlayersIds((prev) =>
                  prev.map((pid, idx) =>
                    idx === index ? player?.id ?? null : pid
                  )
                )
              }
            />
          </Box>
        ))}
        <Box
          sx={{
            flex: "0 0 140px",
            ml: 2,
            display: "flex",
            alignItems: "stretch",
          }}
        >
          <Button
            variant="contained"
            disabled={
              !currentBoardId ||
              !currentRoundId ||
              playersIds.every((id) => id === null)
            }
            onClick={handleCreateMatch}
            sx={{
              height: "56px",
              width: "100%",
              fontWeight: 600,
              borderRadius: 2,
              alignSelf: "stretch",
            }}
          >
            Créer Match
          </Button>
        </Box>
      </Stack>

      <MatchsList />
    </Stack>
  );
};

export default AdminMatchs;
